// 순환 참조 방지를 위해 EventManager 대신 공유 유틸리티에서 import
import { isPersonaEmail, getDisplayEmailFromPersonaEmail } from 'utils/personaEmail'

// TodoFolder 타입 정의
export interface TodoFolder {
  // 필수 속성
  url: string // CalDAV URL (고유 식별자)
  displayName: string // 폴더 표시 이름
  components?: string[] // 지원하는 컴포넌트 (VTODO 등)

  // 선택적 속성
  ctag?: string // 변경 태그
  syncToken?: string // 동기화 토큰
  resourcetype?: string[] // 리소스 타입
  projectedProps?: {
    // 확장 속성
    calendarOrder?: {
      _cdata?: string
    }
    currentUserPrivilegeSet?: {
      privilege?: Array<{
        writeContent?: any
      }>
    }
  }

  // UI 관련 속성
  calendarColor?: { _cdata: string } // 폴더 색상
  order?: number // 정렬 순서
  isDefault?: boolean // 기본 폴더 여부
  isSync?: boolean // 동기화 여부
  isShared?: boolean // 공유 폴더 여부
  isSharedOwner?: boolean // 공유 소유자 여부
  isReadOnly?: boolean // 읽기 전용 여부
  folderType?: string // Todo 폴더 여부
  owner?: string // 소유자 이메일

  // 추가 속성
  userInfo?: TodoUserInfo
}

// todoInfo 타입 정의
export interface TodoInfo {
  folders: TodoFolder[]
}

// 타입 가드 함수
export function isTodoFolder(obj: any): obj is TodoFolder {
  return obj && typeof obj.url === 'string' && typeof obj.displayName === 'string'
}

import { basicColors, stringToHash } from 'utils/ui'
import { TodoUserInfo } from './AccountWithTodoFolders'

// 폴더 생성 헬퍼 함수
export function createTodoFolder(data: Partial<TodoFolder>, userInfo: TodoUserInfo): TodoFolder {
  // calendarColor 기본값 처리
  let calendarColor: { _cdata: string } = { _cdata: '' }
  if (data.calendarColor && typeof (data.calendarColor as any)._cdata === 'string') {
    calendarColor = { _cdata: (data.calendarColor as any)._cdata }
  } else {
    // userInfo.email, displayName 기반 해시 색상
    const hashString = `${data.owner || ''}/${data.displayName || ''}`
    calendarColor = {
      _cdata: basicColors[Math.abs(stringToHash(hashString)) % basicColors.length],
    }
  }

  const isShared = data.resourcetype.includes('shared') || false
  let owner = null
  if (isShared) {
    const urlParts = data.url.split('/')
    const ownerFolderId = urlParts[urlParts.length - 2]
    const hasEmail = ownerFolderId.includes('@')
    if (hasEmail) {
      const splited = ownerFolderId.split('.')
      const email = splited.slice(0, splited.length - 1).join('.')
      if (isPersonaEmail(email)) {
        owner = getDisplayEmailFromPersonaEmail(email)
      } else {
        owner = email
      }
    }
  } else {
    owner = userInfo.email
  }

  return {
    url: data.url || '',
    displayName: data.displayName || '',
    components: data.components || ['VTODO'],
    calendarColor: calendarColor,
    order: data.order,
    isDefault:
      data.isDefault ||
      (data.url && data.url.replace(/\/$/, '').split('/').pop() === 'default_tasks'),
    isSync: false,
    isShared: isShared,
    isSharedOwner: data.resourcetype.includes('sharedOwner') || false,
    isReadOnly:
      !data.projectedProps.currentUserPrivilegeSet.privilege.find((p) => !!p['writeContent']) ||
      false,
    folderType: 'todo',
    owner,
    ctag: data.ctag,
    syncToken: data.syncToken,
    resourcetype: data.resourcetype,
    projectedProps: data.projectedProps,
    userInfo: userInfo,
  }
}
